// 04-11 — una sala secundaria plegada (orden.md regla 12 y su maqueta; plan entrega 3): la tarjeta de carpeta bajo su
// cabecera, como una carpeta de la pantalla de inicio del iPhone. Dentro, las caras de su gente en miniatura (3×3) y
// «N agentes»; en la esquina, la insignia de lo más urgente que le toca a quien mira («🔑 1»): plegar nunca esconde la
// urgencia. Pulsarla (o Intro) la abre y pliega la que estuviera abierta. Una sala sin nadie es solo esta tarjeta, con su
// «+» para contratar ahí. El lienzo pinta la superficie (dibujar.ts, como una sala); aquí, lo que se lee y se pulsa.
import { Plus } from 'lucide-react';
import { memo } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { quien } from '../marco/estado';
import { Avatar } from '../ui';
import type { ZonaMundo } from './generar';
import { estiloDe } from './geometria';
import { PastillaUrgente } from './Letrero';
import type { ResumenLetrero } from './letrero';
import type { MarcadorMundo } from './marcador';

/** Las caras que caben en la tarjeta (3×3, como una carpeta de iOS). */
const CARAS = 9;

export const Carpeta = memo(function Carpeta({
  zona,
  z,
  gente,
  marcas,
  resumen,
  resaltada,
  alAbrir,
  alContratar,
}: {
  zona: ZonaMundo;
  z: number;
  /** Los sentados de la sala (sin los que se van), en su orden. */
  gente: readonly ContratadoDTO[];
  marcas: ReadonlyMap<string, MarcadorMundo>;
  resumen: ResumenLetrero | undefined;
  /** Alguien de dentro está resaltado desde la lista (pasar el ratón por su fila). */
  resaltada: boolean;
  alAbrir: (projectId: string) => void;
  alContratar: (projectId: string) => void;
}) {
  const vacia = gente.length === 0;
  const cuantos = gente.length === 1 ? t('oficinaV2.carpetas.unAgente') : t('oficinaV2.carpetas.agentes', { n: gente.length });
  const etiqueta = vacia
    ? t('oficinaV2.carpetas.contratarEn', { sala: zona.nombre })
    : [t('oficinaV2.carpetas.abrir', { sala: zona.nombre }), resumen?.urgente?.texto, cuantos].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      onClick={() => (vacia ? alContratar(zona.projectId) : alAbrir(zona.projectId))}
      aria-label={etiqueta}
      title={etiqueta}
      className={cn(
        'group absolute flex items-stretch rounded-o-s p-3 text-left outline-none transition-colors duration-150 ease-o-apple hover:bg-oficina-control focus-visible:ring-2 focus-visible:ring-oficina-enlace motion-safe:animate-aparecer',
        resaltada && 'ring-2 ring-oficina-texto2',
      )}
      style={estiloDe(zona.rect, z)}
      data-testid="carpeta"
      data-sala={zona.projectId}
    >
      {vacia ? (
        <span className="flex flex-1 flex-col items-center justify-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-oficina-control text-oficina-texto2 transition-colors duration-150 ease-o-apple group-hover:bg-oficina-pulsado group-hover:text-oficina-texto">
            <Plus className="h-4 w-4" aria-hidden />
          </span>
          <span className="text-o-sub text-oficina-texto2">{t('oficinaV2.carpetas.contratar')}</span>
        </span>
      ) : (
        <>
          <span className="flex flex-1 flex-col items-center justify-center gap-1.5">
            <span className="grid grid-cols-3 gap-1.5" aria-hidden>
              {gente.slice(0, CARAS).map((c) => {
                const m = marcas.get(c.agentId);
                return (
                  <span key={c.agentId} className={cn(m && (m.de || m.aparcado || m.apagado) && 'opacity-40')}>
                    <Avatar nombre={quien(c)} semilla={c.semillaAvatar || c.agentId} tamano={28} />
                  </span>
                );
              })}
            </span>
            <span className="text-o-nota text-oficina-texto2">{cuantos}</span>
          </span>
          {/* La insignia de la carpeta, en su esquina (como el número de una carpeta de iOS). */}
          <PastillaUrgente resumen={resumen} className="absolute -right-2 -top-2 ring-2 ring-oficina-fondo" />
        </>
      )}
    </button>
  );
});
